import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pause, Play, Ban, DollarSign, RefreshCw } from "lucide-react";

import { specialOfferApi } from "@/lib/extensions-api";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { CopyableId } from "@/components/ui/CopyableId";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { StatsCard } from "@/features/customer/components/StatsCard";
import { toast } from "sonner";
import { useState } from "react";

export function SpecialOfferDetailPage() {
  const { pk } = useParams();
  const id = Number(pk);
  const qc = useQueryClient();
  const nav = useNavigate();
  const [cancelOpen, setCancelOpen] = useState(false);

  const detail = useQuery({
    queryKey: ["admin-so-detail", id],
    queryFn: () => specialOfferApi.detail(id),
    enabled: !!id,
  });
  const payouts = useQuery({
    queryKey: ["admin-so-payouts", id],
    queryFn: () => specialOfferApi.payouts(id),
    enabled: !!id,
    refetchInterval: 30_000,
  });

  const pause  = useMutation({ mutationFn: () => specialOfferApi.pause(id),
                                onSuccess: () => refresh("Offer paused.") });
  const resume = useMutation({ mutationFn: () => specialOfferApi.resume(id),
                                onSuccess: () => refresh("Offer resumed.") });
  const cancel = useMutation({ mutationFn: () => specialOfferApi.cancel(id),
                                onSuccess: () => { refresh("Offer cancelled."); setCancelOpen(false); } });

  const trigger = useMutation({
    mutationFn: (payoutId: number) => specialOfferApi.triggerPayout(id, payoutId),
    onSuccess: (r) => {
      toast.success(`Payout outcome: ${r.outcome}`);
      qc.invalidateQueries({ queryKey: ["admin-so-payouts", id] });
      qc.invalidateQueries({ queryKey: ["admin-so-detail", id] });
    },
  });

  const refresh = (msg: string) => {
    toast.success(msg);
    qc.invalidateQueries({ queryKey: ["admin-so-detail", id] });
    qc.invalidateQueries({ queryKey: ["admin-so-payouts", id] });
    qc.invalidateQueries({ queryKey: ["admin-special-offers"] });
  };

  if (detail.isLoading) return <Skeleton className="h-96" />;
  if (!detail.data) return null;
  const o = detail.data;

  const paidPct = Number(o.paid_amount) / Number(o.total_reward) * 100;
  const paidCount = (payouts.data ?? []).filter((p) => p.status === "paid").length;
  const pendingCount = (payouts.data ?? []).filter((p) => p.status === "pending").length;

  return (
    <div className="space-y-6">
      <button onClick={() => nav(-1)}
               className="text-sm text-surface-500 hover:text-surface-800">← Back</button>

      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-surface-500">Special Offer</div>
          <h1 className="text-2xl font-bold text-surface-900">#{o.id}</h1>
          <div className="mt-2 flex items-center gap-3 text-sm">
            <div><CopyableId value={o.target_user_id} /></div>
            <span className="text-surface-400">·</span>
            <span className="font-medium">{o.target_full_name}</span>
            <StatusPill status={o.status} />
          </div>
        </div>
        <div className="flex gap-2">
          {o.status === "active" && (
            <Button variant="outline"
                     leftIcon={<Pause className="h-4 w-4" />}
                     onClick={() => pause.mutate()} loading={pause.isPending}>
              Pause
            </Button>
          )}
          {o.status === "paused" && (
            <Button variant="outline"
                     leftIcon={<Play className="h-4 w-4" />}
                     onClick={() => resume.mutate()} loading={resume.isPending}>
              Resume
            </Button>
          )}
          {["active", "paused"].includes(o.status) && (
            <Button variant="danger"
                     leftIcon={<Ban className="h-4 w-4" />}
                     onClick={() => setCancelOpen(true)}>
              Cancel
            </Button>
          )}
        </div>
      </header>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatsCard icon={DollarSign} tone="info"    label="Total Reward"
                    value={formatCurrency(o.total_reward)} />
        <StatsCard icon={DollarSign} tone="success" label="Paid So Far"
                    value={formatCurrency(o.paid_amount)}
                    hint={`${paidCount} of ${o.duration_days} installments`} />
        <StatsCard icon={DollarSign} tone="warning" label="Remaining"
                    value={formatCurrency(o.remaining_amount)}
                    hint={`${pendingCount} pending`} />
        <StatsCard icon={DollarSign} tone="neutral" label="Daily Payout"
                    value={formatCurrency(o.daily_amount)} />
      </div>

      <Card>
        <CardHeader><CardTitle>Progress</CardTitle></CardHeader>
        <CardBody>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-surface-600">Paid</span>
            <span className="font-mono">
              {formatCurrency(o.paid_amount)} / {formatCurrency(o.total_reward)}
            </span>
          </div>
          <div className="h-3 bg-surface-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-success-500 to-success-600 transition-all"
                  style={{ width: `${paidPct}%` }} />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
            <Row k="Start"      v={formatDateTime(o.start_date)} />
            <Row k="End"        v={formatDateTime(o.end_date)} />
            <Row k="Created by" v={o.admin_user_id} />
          </div>
          {o.note && (
            <div className="mt-3 p-3 bg-surface-50 rounded-lg text-sm text-surface-700 border border-surface-200">
              <div className="text-2xs font-semibold uppercase text-surface-500 mb-1">Note</div>
              {o.note}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Payout Schedule</CardTitle></CardHeader>
        <CardBody className="p-0">
          {payouts.isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Day</TH>
                  <TH>Scheduled Date</TH>
                  <TH className="text-right">Amount</TH>
                  <TH>Status</TH>
                  <TH>Processed</TH>
                  <TH></TH>
                </TR>
              </THead>
              <tbody>
                {(payouts.data ?? []).map((p) => (
                  <TR key={p.id}>
                    <TD className="font-mono">#{p.sequence_no}</TD>
                    <TD className="text-sm">{formatDateTime(p.scheduled_date)}</TD>
                    <TD className="text-right font-semibold">
                      {formatCurrency(p.scheduled_amount)}
                    </TD>
                    <TD><StatusPill status={p.status} /></TD>
                    <TD className="text-xs text-surface-500">
                      {p.processed_at ? formatDateTime(p.processed_at) : "—"}
                    </TD>
                    <TD>
                      {p.status === "pending" &&
                       new Date(p.scheduled_date) <= new Date() && (
                        <Button size="sm" variant="ghost"
                                 leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
                                 onClick={() => trigger.mutate(p.id)}
                                 loading={trigger.isPending}>
                          Trigger
                        </Button>
                      )}
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Cancel Special Offer?"
        message="Pending payouts will be skipped. Already paid installments remain immutable."
        confirmLabel="Cancel Offer"
        destructive
        loading={cancel.isPending}
        onConfirm={() => cancel.mutate()}
      />
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div className="text-2xs font-semibold uppercase text-surface-500">{k}</div>
      <div className="mt-0.5 font-medium text-surface-800">{v}</div>
    </div>
  );
}
